import axios from 'axios'
import { useState } from 'react'

import useApi from '../useApi'
import { UPLOAD_ATTACHMENTS_TYPES } from 'common'

type Params = {
  attachmentType: ValueOf<typeof UPLOAD_ATTACHMENTS_TYPES>
}

export type UploadHandler = (params: {
  fileName: string
  file: Blob
  contentType: string
  _id?: string
  onProgress?: ({ name, progress }: Progress) => void
}) => Promise<string | void>

interface Progress {
  name: string
  progress: number
}

const useUpload = ({ attachmentType }: Params): { uploadHandler: UploadHandler; error: boolean } => {
  const { handleApi } = useApi({ method: 'POST', url: '/attachments/upload' })
  const [error, setError] = useState(false)

  const uploadToS3 = async ({
    file,
    url,
    contentType,
    fileName,
    onProgress
  }: {
    file: Blob
    url: string
    contentType: string
    fileName: string
    onProgress?: ({ name, progress }: Progress) => void
  }) => {
    const handleProgress = (progressEvent: ProgressEvent) => {
      const percent = Math.floor((progressEvent.loaded / progressEvent.total) * 100)
      const payload = { name: fileName, progress: percent }
      onProgress && onProgress(payload)
    }

    try {
      await axios({
        data: file,
        url,
        method: 'PUT',
        headers: {
          'Content-Type': contentType
        },
        onUploadProgress: handleProgress
      })
    } catch (error) {
      console.error(error)
    }
  }

  interface AttachmentUploadResponse {
    url: string
    key: string
  }

  const uploadHandler: UploadHandler = async ({ fileName, file, contentType, _id, onProgress }) => {
    const { response, isError } = await handleApi<AttachmentUploadResponse>({
      attachmentType,
      fileName,
      contentType,
      _id
    })

    if (isError) return setError(isError)

    if (response && file) {
      const { url, key } = response
      await uploadToS3({ file, url, contentType, fileName, onProgress })
      return key
    }
  }

  return { uploadHandler, error }
}

export { useUpload }
